// THE NAME THIS PORTAL SHOWS A READER — decided in ONE place, because a second
// spelling is how two screens come to disagree.
//
// Registrar, 2026-08-16: the Synod Admin, who RUNS a sitting, sees
// "Decision Dashboard"; everybody else sees "Decision Portal". `CCA-decides` was
// the estate's only product codename on a portal header — every sibling wears a
// human name (Registrar Dashboard, Finance Portal, Parish Portal) — and this
// brings it into line.
//
// THIS IS DISPLAY TEXT AND NOTHING ELSE. The package, the Pages project, the
// worker's `portal: 'decides'` outbox key, the `--brand-decides` token and the
// `decides` portal id in @cca/portals are all unchanged and must stay that way:
// a name a person reads and an identifier a machine matches on are two things.
//
// ── WHAT IT SAYS WHEN NOBODY IS SIGNED IN ──────────────────────────────────
// The general name, and that is a decision rather than a fallback. The sign-in
// screen, the tab title, the manifest and the login-code email are all seen
// before any role exists, and two things follow:
//
//   • "Decision Portal" is the general case AND the Scrutineer's name, so the
//     word does not change under a Scrutineer's feet the moment they sign in.
//     A header that flips at sign-in reads as a glitch, and the only reader it
//     would flip for is the one with the least standing.
//   • It tells an unauthenticated visitor nothing. "Decision Dashboard" on the
//     door would announce that an admin surface exists behind it, to somebody
//     who has not been asked who they are.
//
// The worker holds its own copy of that decision for the login-code email
// (`PORTAL_NAME`, packages/cca-decides-worker/src/auth.ts) — a Worker and a
// browser bundle share no module, the same reason the accent colour is written
// twice. Change one, change the other.

import type { Identity, StandingSitting } from './api/client';

/** What the Synod Admin, who runs a sitting, is shown. */
const ADMIN_PORTAL_NAME = 'Decision Dashboard';

/**
 * What everybody else is shown, and what every surface says before anybody is
 * known. Exported because a few sentences of copy name the portal in prose, and
 * a literal in a sentence is exactly the second spelling this module exists to
 * prevent.
 */
export const GENERAL_PORTAL_NAME = 'Decision Portal';

/**
 * The name for one reader whose role the SERVER has already stated. `null` means
 * nobody is known — signed out, still checking, no grant, or the server
 * unreachable — and gets the general name.
 *
 * The role word is `role_grant.role` as resolved server-side on the request that
 * produced it. Nothing here infers a role from the token, from storage, or from
 * what the screen happens to be able to see.
 */
export function portalNameForRole(role: StandingSitting['role'] | null): string {
  return role === 'admin' ? ADMIN_PORTAL_NAME : GENERAL_PORTAL_NAME;
}

/**
 * The name for the person `GET /api/auth/me` last described.
 *
 * ADMIN WINS WHERE A PERSON HOLDS BOTH, which is the SAME precedence the results
 * gate applies (`strongestRole`, packages/cca-decides-worker/src/grants.ts) and
 * is written the same way — an explicit test for 'admin', not a sort, so it does
 * not invert silently the day a role is renamed.
 *
 * It is asked PER PERSON where that one is asked per sitting, and deliberately:
 * somebody who runs one sitting and scrutineers another runs this platform, and
 * a header that changed word as they moved between sittings would be furniture
 * that looks like a status. What may be done IN a sitting is a different
 * question, answered by the server on the request that asks — the header is not
 * a permission and this function decides nothing but a word.
 */
export function portalNameFor(identity: Identity | null): string {
  if (identity === null) return GENERAL_PORTAL_NAME;
  return portalNameForRole(
    identity.sittings.some((sitting) => sitting.role === 'admin') ? 'admin' : 'scrutineer',
  );
}
